import { z } from 'zod';

import type { OdooEventFilters, ReconciliationStatus } from './types';

export const SyncTabSchema = z.enum(['abandoned', 'pending', 'sent', 'all']);
export type SyncTab = z.infer<typeof SyncTabSchema>;

export const DEFAULT_SYNC_TAB: SyncTab = 'abandoned';
export const SYNC_PAGE_SIZE = 25;

/**
 * State lives in the URL so a colleague can be sent the exact list on screen.
 * A malformed value falls back to "unset" instead of failing route validation:
 * a stale bookmark should land on the default view, not an error page.
 */
export const SyncSearchSchema = z.object({
  status: SyncTabSchema.optional().catch(undefined),
  eventType: z.string().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  contractId: z.string().uuid().optional().catch(undefined),
});
export type SyncSearch = z.infer<typeof SyncSearchSchema>;

export function filtersFromSearch(search: SyncSearch): OdooEventFilters {
  const tab = search.status ?? DEFAULT_SYNC_TAB;
  const term = search.search?.trim();
  return {
    status: tab === 'all' ? undefined : tab,
    eventType: search.eventType || undefined,
    search: term ? term : undefined,
    contractId: search.contractId,
    page: search.page ?? 1,
    pageSize: SYNC_PAGE_SIZE,
  };
}

/** Link target for "show the events of this one record". */
export const syncSearchForSubject = (
  subjectId: string
): Pick<SyncSearch, 'status' | 'search'> => ({
  status: 'all',
  search: subjectId,
});

/** Every event of one contract: its invoice, payments, cancellation and write-off. */
export const syncSearchForContract = (
  contractId: string
): Pick<SyncSearch, 'status' | 'contractId'> => ({
  status: 'all',
  contractId,
});

/** Where a reconciliation status sends the operator in Sync Health. */
export const syncSearchForStatus = (
  status: ReconciliationStatus
): Pick<SyncSearch, 'status'> => ({
  status:
    status === 'sync_failed'
      ? 'abandoned'
      : status === 'sync_pending'
        ? 'pending'
        : 'all',
});
